import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { RequestFormField } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

export interface RequestFormFieldsProps {
  fields: RequestFormField[]
  values: Record<string, unknown>
  onChange: (next: Record<string, unknown>) => void
  disabled?: boolean
}

/**
 * A form nobody wrote, rendered from what an administrator defined.
 *
 * <p>The server validates against the same definitions, so the constraints here are the same
 * constraints — `required`, `min`, `max`, `pattern` all come off the field rather than being
 * restated. That matters: a client that invents its own rules is a client that disagrees with
 * the server, and the person filling the form finds out on submit.
 *
 * <p>A field with no definition is not rendered. The payload only ever carries what the form
 * asked for, because anything else is refused on the way in.
 */
/**
 * A payload value as an input's `value`.
 *
 * <p>Narrowed rather than `String(value)`: the payload is validated jsonb, so a value can be
 * an object, and the default stringification of one is `[object Object]` — which would then
 * be submitted back as the field's new value.
 */
function asText(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

export function RequestFormFields({ fields, values, onChange, disabled }: RequestFormFieldsProps) {
  function set(code: string, value: unknown) {
    onChange({ ...values, [code]: value })
  }

  if (fields.length === 0) {
    return (
      <p className="text-sm text-[var(--color-muted)]">
        This request type asks for nothing beyond a subject.
      </p>
    )
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {fields.map((field) => {
        const id = `field-${field.code}`
        const value = values[field.code]

        return (
          <div key={field.id} className="space-y-1.5">
            <Label htmlFor={id}>
              {field.label}
              {!field.required && (
                <span className="ms-1 text-xs font-normal text-[var(--color-muted)]">(optional)</span>
              )}
            </Label>

            {field.fieldType === 'DROPDOWN' ? (
              <Select
                id={id}
                required={field.required}
                disabled={disabled}
                value={typeof value === 'string' ? value : ''}
                onChange={(event) => set(field.code, event.target.value || null)}
                className="w-full"
              >
                <option value="">Choose…</option>
                {field.options.map((option, index) => (
                  <option key={option.value ?? index} value={option.value ?? ''}>
                    {option.label ?? option.value ?? ''}
                  </option>
                ))}
              </Select>
            ) : field.fieldType === 'DATE' ? (
              // The app's picker, not the browser's: one calendar button and the company's
              // format, rather than whatever the browser's locale decides.
              <DatePicker
                id={id}
                required={field.required}
                disabled={disabled}
                min={field.minDate ?? undefined}
                max={field.maxDate ?? undefined}
                value={asText(value)}
                onChange={(event) => set(field.code, event.target.value === '' ? null : event.target.value)}
              />
            ) : (
              <Input
                id={id}
                type={field.fieldType === 'NUMBER' ? 'number' : 'text'}
                required={field.required}
                disabled={disabled}
                // Every constraint comes off the definition the server validates against, so
                // the two cannot disagree.
                min={field.fieldType === 'NUMBER' ? field.minNumber ?? undefined : undefined}
                max={field.fieldType === 'NUMBER' ? field.maxNumber ?? undefined : undefined}
                minLength={field.minLength ?? undefined}
                maxLength={field.maxLength ?? undefined}
                pattern={field.regex ?? undefined}
                value={asText(value)}
                onChange={(event) =>
                  set(
                    field.code,
                    event.target.value === ''
                      ? null
                      : field.fieldType === 'NUMBER'
                        ? Number(event.target.value)
                        : event.target.value,
                  )
                }
              />
            )}

            {field.helpText && (
              <p className="text-xs text-[var(--color-muted)]">{field.helpText}</p>
            )}
          </div>
        )
      })}
    </div>
  )
}
